import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '../services/supabase'
import { WordPressService, getWordPressSettings } from '../services/wordpressService'
import { videoService } from '../services/videoService'
import { useImageSourceStore } from '../stores/imageSource'

export const useArticleStore = defineStore('article', () => {
  const articles = ref([])
  const loading = ref(false)
  const currentArticle = ref(null)
  const articleVideo = ref(null)
  const videoPosition = ref(null)
  const isAutoPublishing = ref(false)

  const canEditVideo = computed(() => !isAutoPublishing.value)

  const saveArticle = async ({ title, content, keyword, seed_keyword, featuredImage, searchIntent, language_code }) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      if (!language_code) {
        console.error('No language code provided for article:', { title, keyword })
        throw new Error('Language code is required')
      }

      console.log('Saving article:', { 
        title, 
        keyword, 
        seed_keyword, 
        searchIntent,
        language_code 
      })

      // Fetch the correct seed_keyword from keywords table
      const { data: keywordData, error: keywordError } = await supabase
        .from('keywords')
        .select('term, seed_keyword, language_code')
        .eq('term', keyword)
        .single()

      if (keywordError) {
        console.error('Error fetching keyword data:', keywordError)
      } else if (keywordData?.language_code !== language_code) {
        console.warn('Language code mismatch:', {
          article: language_code,
          keyword: keywordData.language_code
        })
      }

      const articleData = {
        title,
        content,
        keyword,
        seed_keyword: keywordData?.seed_keyword || seed_keyword || keyword,
        user_id: user.id,
        search_intent: searchIntent ? {
          primary: searchIntent.primary,
          secondary: searchIntent.secondary || []
        } : null,
        language_code,
        video: articleVideo.value ? {
          ...articleVideo.value,
          position: videoPosition.value
        } : null
      }

      if (featuredImage) {
        const imageSourceStore = useImageSourceStore()
        const imageSource = imageSourceStore.imageSource

        articleData.featured_image = {
          url: featuredImage.urls.raw,
          thumb_url: featuredImage.urls.thumb,
          alt: featuredImage.description || featuredImage.photographer,
          photographer: featuredImage.user.name,
          photographer_url: featuredImage.user.portfolio_url,
          source: imageSource
        }
      }

      // Check if WordPress auto-publish is enabled
      const wpSettings = await getWordPressSettings()
      if (wpSettings?.auto_publish) {
        try {
          const wordpress = new WordPressService(
            wpSettings.site_url,
            wpSettings.username,
            wpSettings.password
          )
          await wordpress.createPost(articleData)
        } catch (error) {
          console.error('Failed to auto-publish to WordPress:', error)
          // Continue with normal save even if WordPress fails
        }
      }

      const { data, error } = await supabase
        .from('articles')
        .insert([articleData])
        .select(`
          id,
          title,
          content,
          keyword,
          seed_keyword,
          user_id,
          search_intent,
          video,
          featured_image,
          created_at,
          updated_at
        `)
        .single()

      if (error) {
        console.error('Error saving article:', error)
        throw error
      }

      // Update the local articles list
      articles.value = [data, ...articles.value]

      console.log('Article saved successfully:', data)
      return data
    } catch (error) {
      console.error('Failed to save article:', error)
      throw error
    }
  }

  const updateArticle = async (id, updates) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('User not authenticated')

      console.log('Updating article:', { id, updates })

      // If we're updating content, use the update_article function
      if (updates.content) {
        const { data, error } = await supabase
          .rpc('update_article', {
            article_id: id,
            user_id: user.id,
            content: updates.content,
            updated_at: new Date().toISOString()
          })

        if (error) {
          console.error('Error updating article:', error)
          throw error
        }

        if (!data || data.length === 0) {
          throw new Error('No article found or no permission to update')
        }

        // Update the article in the local list
        const index = articles.value.findIndex(a => a.id === id)
        if (index !== -1) {
          articles.value[index] = { ...articles.value[index], ...data[0] }
        }

        return data[0]
      } else {
        // For other updates (like featured image), use the regular update
        const { data, error } = await supabase
          .from('articles')
          .update(updates)
          .eq('id', id)
          .eq('user_id', user.id)
          .select('*')
          .single()

        if (error) {
          console.error('Error updating article:', error)
          throw error
        }

        if (!data) {
          throw new Error('No article found or no permission to update')
        }

        // Update the article in the local list
        const index = articles.value.findIndex(a => a.id === id)
        if (index !== -1) {
          articles.value[index] = { ...articles.value[index], ...data }
        }

        return data
      }
    } catch (error) {
      console.error('Failed to update article:', error)
      throw error
    }
  }

  const fetchUserArticles = async () => {
    loading.value = true
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      articles.value = data
    } catch (error) {
      console.error('Error fetching articles:', error)
    } finally {
      loading.value = false
    }
  }

  const fetchArticle = async (id) => {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single()
      
      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching article:', error)
      return null
    }
  }

  const publishToWordPress = async (article) => {
    const wpSettings = await getWordPressSettings()
    if (!wpSettings) {
      throw new Error('WordPress settings not configured')
    }

    const wordpress = new WordPressService(
      wpSettings.site_url,
      wpSettings.username,
      wpSettings.password
    )

    // Ensure we have all required data
    if (!article.title || !article.content) {
      throw new Error('Article must have both title and content')
    }

    // Prepare the article data with featured image
    const articleData = {
      title: article.title,
      content: article.content,
      keyword: article.keyword || '',
      seed_keyword: article.seed_keyword || '',
      featured_image: article.featured_image ? {
        url: article.featured_image.url,
        alt: article.featured_image.alt || '',
        description: article.featured_image.alt || ''
      } : null
    }

    console.log('Publishing article with data:', {
      ...articleData,
      content: articleData.content.substring(0, 100) + '...' // Log truncated content
    })

    try {
      const result = await wordpress.createPost(articleData)
      console.log('Successfully published to WordPress:', result)
      return result
    } catch (error) {
      console.error('Failed to publish to WordPress:', error)
      throw error
    }
  }

  function setArticle(article) {
    currentArticle.value = article
    if (article?.video) {
      articleVideo.value = article.video
      videoPosition.value = article.video.position
    } else {
      articleVideo.value = null
      videoPosition.value = null
    }
  }

  function setVideo(video, position = 'after-introduction') {
    articleVideo.value = video
    videoPosition.value = position
  }

  function removeVideo() {
    articleVideo.value = null
    videoPosition.value = null
  }

  function setAutoPublishing(value) {
    isAutoPublishing.value = value
  }

  async function searchRelatedVideo(query) {
    try {
      const videos = await videoService.searchVideos(query)
      return videos
    } catch (error) {
      console.error('Error searching related video:', error)
      throw error
    }
  }

  function getArticleWithVideo() {
    if (!currentArticle.value) return null
    if (!articleVideo.value) return currentArticle.value

    const content = { ...currentArticle.value }
    const videoHtml = generateVideoHtml(articleVideo.value)
    
    switch (videoPosition.value) {
      case 'after-introduction':
        content.html = insertAfterFirstParagraph(content.html, videoHtml)
        break
      case 'before-conclusion':
        content.html = insertBeforeLastParagraph(content.html, videoHtml)
        break
      case 'middle':
        content.html = insertInMiddle(content.html, videoHtml)
        break
    }

    return content
  }

  function generateVideoHtml(video) {
    return `
      <div class="video-container my-6">
        <video 
          controls 
          preload="metadata"
          poster="${video.thumbnail}"
          class="w-full rounded-lg"
        >
          <source src="${video.url}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      </div>
    `
  }

  function insertAfterFirstParagraph(html, videoHtml) {
    const paragraphs = html.split('</p>')
    if (paragraphs.length < 2) return html + videoHtml
    return paragraphs[0] + '</p>' + videoHtml + paragraphs.slice(1).join('</p>')
  }

  function insertBeforeLastParagraph(html, videoHtml) {
    const paragraphs = html.split('</p>')
    if (paragraphs.length < 2) return videoHtml + html
    return paragraphs.slice(0, -1).join('</p>') + '</p>' + videoHtml + paragraphs[paragraphs.length - 1]
  }

  function insertInMiddle(html, videoHtml) {
    const paragraphs = html.split('</p>')
    const middleIndex = Math.floor(paragraphs.length / 2)
    return paragraphs.slice(0, middleIndex).join('</p>') + '</p>' + videoHtml + paragraphs.slice(middleIndex).join('</p>')
  }

  return {
    articles,
    loading,
    currentArticle,
    articleVideo,
    videoPosition,
    isAutoPublishing,
    canEditVideo,
    saveArticle,
    updateArticle,
    fetchUserArticles,
    fetchArticle,
    publishToWordPress,
    setArticle,
    setVideo,
    removeVideo,
    setAutoPublishing,
    searchRelatedVideo,
    getArticleWithVideo
  }
})
